// Dev Mode on screen (docs/features/dev-mode.md): the row in the ⋯ sheet (one switch per dev Mac that offers) and
// the strip at the very top while it is on. Plain DOM beside the page's own chrome, made here — index.html is not touched.
import { COLOR, SCREEN } from '../core/tokens.js'

const esc = (s) => String(s ?? '').replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]))

// The strip: one thin line pinned to the very top, the design tokens' system orange; the page's top bar and its ⋯
// sheet move down by its height while it shows, so nothing is covered. On a touch screen it is a full 44 px target tall
// (design/tokens.json › screen.minTarget), so Off can be tapped (Josh 10-02: "make sure all screens and buttons … are
// accessible").
const STRIP = 20
const STYLE = `
:root { --devcode-strip: ${STRIP}px; }
@media (pointer: coarse) { :root { --devcode-strip: ${SCREEN.minTarget}px; } }
#devcode-row { margin: 10px 0 4px; padding-top: 10px; border-top: 1px solid var(--line); }
#devcode-row[hidden], #devcode-banner[hidden] { display: none; }
#devcode-row .head { font: 600 13px/1 ui-monospace, Menlo, monospace; letter-spacing: .08em; text-transform: uppercase; color: var(--faint); margin: 0 0 8px; }
#devcode-row label { display: flex; align-items: center; gap: 8px; min-height: ${SCREEN.minTarget}px; font-size: 14px; color: var(--ink); cursor: pointer; margin: 0 0 6px; }
#devcode-row label input { width: 22px; height: 22px; }
#devcode-row .hint { margin: 0; font-size: 13px; color: var(--dim); }
#devcode-banner { position: fixed; top: 0; left: 0; right: 0; height: calc(var(--devcode-strip) + env(safe-area-inset-top, 0px)); padding: env(safe-area-inset-top, 0px) 4px 0 10px;
  z-index: 50; display: flex; align-items: center; gap: 6px; background: ${COLOR.systemOrange}; color: #1c1300;
  font: 600 11px/1 ui-monospace, Menlo, monospace; line-height: var(--devcode-strip); letter-spacing: .02em; }
#devcode-banner .t { flex: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
#devcode-banner .n { font-weight: 500; opacity: .8; }
#devcode-banner button { appearance: none; border: 0; border-radius: 4px; background: rgba(0,0,0,.18); color: inherit; font: inherit; line-height: 16px; padding: 0 7px; cursor: pointer; flex: none; }
@media (pointer: coarse) {
  #devcode-banner { font-size: 13px; }
  #devcode-banner button { min-height: ${SCREEN.minTarget}px; min-width: ${SCREEN.minTarget}px; padding: 0 12px; border-radius: 0; }
}
#devcode-banner button:focus-visible { outline: 3px solid #1c1300; outline-offset: -3px; }
body.devcode-strip #top { top: calc(var(--devcode-strip) + 6px + env(safe-area-inset-top, 0px)); }
body.devcode-strip #sheet { top: calc(var(--devcode-strip) + 64px + env(safe-area-inset-top, 0px)); }
`

let made = null
function make() {
  if (made) return made
  const style = document.createElement('style')
  style.textContent = STYLE
  document.head.appendChild(style)
  const row = document.createElement('div')
  row.id = 'devcode-row'
  row.hidden = true
  const sheet = document.getElementById('sheet')
  sheet?.insertBefore(row, document.getElementById('build'))
  const banner = document.createElement('div')
  banner.id = 'devcode-banner'
  banner.hidden = true
  banner.setAttribute('role', 'status')
  document.body.appendChild(banner)
  made = { row, banner }
  return made
}

/// The ⋯ sheet's row: `choices` [{ key, label, on }]; `onToggle(key, on)`.
export function drawRow(choices, onToggle) {
  const { row } = make()
  row.hidden = !choices.length
  if (!choices.length) { row.innerHTML = ''; return }
  row.innerHTML = '<p class="head">Dev Mode</p>' + choices.map((c) =>
    `<label><input type="checkbox" data-mac="${esc(c.key)}" ${c.on ? 'checked' : ''}> <span>${esc(c.label)}</span></label>`).join('') +
    '<p class="hint">This browser only. The page reloads with that Mac’s copy of the web app; off goes back to the site’s.</p>'
  for (const box of row.querySelectorAll('input[data-mac]')) box.onchange = () => onToggle(box.dataset.mac, box.checked)
}

/// The banner: `text`, an optional `note` after it, and its buttons [{ label, act }] (Off is always one).
export function drawBanner(state) {
  const { banner } = make()
  banner.hidden = !state
  document.body.classList.toggle('devcode-strip', !!state)
  if (!state) { banner.innerHTML = ''; return }
  banner.innerHTML = `<span class="t">${esc(state.text)}${state.note ? ` <span class="n">· ${esc(state.note)}</span>` : ''}</span>` +
    state.buttons.map((b, i) => `<button data-i="${i}">${esc(b.label)}</button>`).join('')
  for (const el of banner.querySelectorAll('button')) el.onclick = () => state.buttons[+el.dataset.i].act()
  banner.title = state.text + (state.note ? ' · ' + state.note : '')
}
